import { useMemo } from 'react'
import { LinkingItem, LinkingItemType, Group } from '../../../components/Linking/Linking.types'
import { LINKING_GROUPING_CONFIGS } from '../../configs/linkingGroupingConfigs'

type UseItemGroupingResult<T = object> = {
    groups: Group<T>[]
    isLoading: boolean
}

export const useItemGrouping = <T = object>(
    linkingItems: LinkingItem<T>[] | undefined,
    linkingType: LinkingItemType
): UseItemGroupingResult<T> => {
    const groupingConfig = LINKING_GROUPING_CONFIGS[linkingType]

    const response = groupingConfig?.useQuery?.()
    const data = groupingConfig?.parseQueryResponse?.(response)

    const isLoading = data && 'isLoading' in data ? data.isLoading : false

    const groups = useMemo(() => {
        if (!groupingConfig || isLoading || !linkingItems?.length || !data) {
            return []
        }

        const groups = new Map<Group['id'], Group<T>>()

        linkingItems.forEach((linkingItem) => {
            const group = groupingConfig.getGroupData(linkingItem, data)
            if (!group) return

            if (!groups.has(group.id)) {
                groups.set(group.id, {
                    ...group,
                    items: [],
                })
            }

            groups.get(group.id).items.push(linkingItem)
        })

        return Array.from(groups.values()).sort((a, b) => a.order - b.order)
    }, [linkingItems, groupingConfig, data, isLoading])

    return {
        groups,
        isLoading,
    }
}
